import { inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { LocalStorageService } from '@core/services/local-storage.service';
import { getBrowserLang, LangDefinition, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { LocalStorageType } from '@core/enums';
import { AVAILABLE_LANGS } from '@translate';

export const WINDOW = new InjectionToken<Window | undefined>(
  'WindowToken',
  typeof window !== 'undefined' && window.document
    ? { providedIn: 'root', factory: () => window }
    : { providedIn: 'root', factory: () => undefined },
);

@Injectable({
  providedIn: 'root',
})
export class LanguageService implements OnDestroy {
  private readonly localStorage = inject(LocalStorageService);
  private readonly translocoService = inject(TranslocoService);
  private langChangesSubject = new BehaviorSubject<string>(LanguageService.readPreferredLanguage());

  langChanges$: Observable<string> = this.langChangesSubject.asObservable();

  ngOnDestroy() {
    this.langChangesSubject.complete();
  }

  setLanguage(lang: string) {
    if (null == lang || lang.length === 0) {
      return;
    }

    this.localStorage.removeItem(LocalStorageType.LANG);
    this.localStorage.setItem(LocalStorageType.LANG, lang);

    this.translocoService.setActiveLang(lang);
    this.langChangesSubject.next(lang);
  }

  getSelectedLanguage(): string {
    return this.langChangesSubject.getValue();
  }

  getLanguages(): Observable<Map<string, string>> {
    const langArr = new Map<string, string>();
    this.translocoService.getAvailableLangs().map(lang => {
      return langArr.set(
        typeof lang === 'string' ? (lang as string) : (lang as LangDefinition)?.id,
        typeof lang === 'string' ? (lang as string) : (lang as LangDefinition)?.id,
      );
    });

    return of(langArr);
  }

  static readPreferredLanguage(): string {
    // URL param
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');

    if (urlLang && AVAILABLE_LANGS.findIndex(lang => lang.id === urlLang) !== -1) {
      localStorage.setItem(LocalStorageType.LANG, JSON.stringify(urlLang));
      return urlLang;
    }

    // we need to use plain `localStorage` without a window
    // const preferredLanguage = localStorage.getItem(LocalStorageType.LANG);
    // const isPreferredLanguageAvailable = preferredLanguage
    //   ? AVAILABLE_LANGS.some(lang => lang.id === JSON.parse(preferredLanguage))
    //   : false;
    //
    // // set preferred lang if exist
    // if (null != preferredLanguage && isPreferredLanguageAvailable) {
    //   return JSON.parse(preferredLanguage);
    // }

    const browserLanguage = getBrowserLang();
    const isBrowserLanguageAvailable = browserLanguage
      ? AVAILABLE_LANGS.some(lang => lang.id === browserLanguage.toLowerCase())
      : false;

    // set preferred lang from browser culture
    if (null != browserLanguage && isBrowserLanguageAvailable) {
      return browserLanguage;
    }

    // set default lang as preferred
    return 'pl';
  }
}
